import { useState, useEffect, useMemo } from 'react';
import '../../styles/PageCommon.css';
import './DistributionPage.css';
import { ReactComponent as DownloadIcon } from '../../assets/icons/download-icon.svg';
import InfoTooltip from '../../components/common/InfoTooltip/InfoTooltip';
import { useNavigate } from "react-router-dom";
import { distributionService } from '../../services/distributionService';
import { ReactComponent as LoadingIcon } from '../../assets/icons/upload-loading-icon.svg';
import LoadingAnimation from '../../components/common/LoadingAnimation/LoadingAnimation';
import { NotificationContainer } from '../../components/common/NotificationContainer';
import NotificationModal from '../../components/common/NotificationModal';
import { parseApiError } from '../../utils/parseErrorMessage';
import Pagination from '../../components/common/Pagination/Pagination';
import { useFilterParams } from '../../hooks/useFilterParams';
import { formatToAbbrevMonthYear, numberToKiloFormat } from '../../utils/utils';
import ColumnFilterDropdown from '../../components/common/ColumnFilterDropdown';
import BatteryCell from '../../components/common/BatteryCell';
import { AccordionCheckboxSelector } from '../../components/common/AccordionCheckboxSelector/AccordionCheckboxSelector';
import { ReactComponent as ArrowDownIcon } from '../../assets/icons/arrow-down-icon.svg';
import SegmentedControl from '../../components/common/SegmentedControl/SegmentedControl';

const COLUMNS = [
  // { key: 'hub_name',    label: 'Склад' },
  { key: 'branch_name', label: 'Склад' },
  { key: 'target_amount_dsp_per_branch', label: 'План ₸' },
  { key: 'available_amount_kzt_per_branch', label: 'Остаток ₸' },
  { key: 'recommended_amount_kzt_per_branch', label: 'Рекомендуемое распределение ₸' },
  { key: 'adjusted_amount_kzt_per_branch', label: 'Сумма распределения' },
  { key: 'total_adjusted_volume_cbm_per_branch', label: 'Объем' },
  { key: 'total_adjusted_gross_weight_kg_per_branch', label: 'Вес' },
  { key: 'readiness_for_target_per_branch', label: 'Готовность к плану' },
];

const PRODUCT_FILTER_KEYS = ['sku_code', 'brand', 'category', 'sub_category', 'subline'];

const DEFAULT_FILTERS = {
  page: 1,
  page_size: 10,
  view_type: "dsp",
  ...Object.fromEntries(PRODUCT_FILTER_KEYS.map((key) => [key, []])),
  ...Object.fromEntries(COLUMNS.map(({ key }) => [key, []])),
};

export default function DistributionPage() {
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const navigate = useNavigate();

  const [isProductsFilterOpen, setIsProductsFilterOpen] = useState(false);
  const [planningDate, setPlanningDate] = useState(null);
  const [distributionTotalItems, setDistributionTotalItems] = useState(0);
  const [distributionData, setDistributionData] = useState([]);
  const [distributionSummary, setDistributionSummary] = useState(null);
  const [summaryInfo, setSummaryInfo] = useState(null);
  const [isUpdated, setIsUpdated] = useState(false);
  const [distributionError, setDistributionError] = useState("")

  // eslint-disable-next-line no-unused-vars
  const [editingDistributionRows, setEditingDistributionRows] = useState({});
  const [filterOptions, setFilterOptions] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  const distributionKey = (row, index) => `${row.branch_name}_${index}`;

  // --- Filters State and Logic ---
  const { filters, updateFilters } = useFilterParams(DEFAULT_FILTERS);
  const productFilters = useMemo(
    () => Object.fromEntries(PRODUCT_FILTER_KEYS.map((key) => [key, filters[key] || []])),
    [filters],
  );
  const productFiltersArray = Object.values(productFilters).flat();

  const colOptions = useMemo(() => {
    const result = {};
    Object.entries(filterOptions).forEach(([key, values]) => {
      result[key] = (values || []).map(String);
    });
    return result;
  }, [filterOptions]);

  const handleColFilterApply = (key, vals) => updateFilters({ [key]: vals, page: 1 });
  const getColSelected = (key) => filters[key] || [];

  const openProductsFilter = () => {
    setIsProductsFilterOpen(true);
  };


  // Summary has no params — fetch once on mount.
  useEffect(() => {
    let cancelled = false;
    distributionService.getDistributionSummary()
      .then(res => { if (!cancelled && res) setDistributionSummary(res); })
      .catch(err => { if (!cancelled) setDistributionError(parseApiError(err) || ""); });
    distributionService.getSummaryInformationIcon()
      .then(res => { if (!cancelled && res) setSummaryInfo(res); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Table data — re-fetches on filter change or after a save (refreshKey).
  useEffect(() => {
    let cancelled = false;
    setIsLoadingData(true);
    const columnFilters = Object.fromEntries(
      COLUMNS.map(({ key }) => [key, filters[key]]).filter(([, v]) => v?.length)
    );
    distributionService.getData(filters.page, filters.page_size, filters.view_type, columnFilters, productFilters)
      .then(res => {
        if (cancelled) return;
        if (res.total_items) setDistributionTotalItems(res.total_items);
        if (res.items) setDistributionData(res.items);
        if (res.filter_options) setFilterOptions(res.filter_options);
        if (res.planning_date) setPlanningDate(res.planning_date);
      })
      .catch(err => { if (!cancelled) setDistributionError(parseApiError(err) || ""); })
      .finally(() => { if (!cancelled) setIsLoadingData(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.page, filters.page_size, filters.view_type,
    filters.branch_name, filters.target_amount_dsp_per_branch,
    filters.available_amount_kzt_per_branch, filters.recommended_amount_kzt_per_branch,
    filters.adjusted_amount_kzt_per_branch, filters.readiness_for_target_per_branch,
    filters.sku_code, filters.brand,
    filters.category, filters.sub_category,
    filters.subline,
    refreshKey,
  ]);
  
  // eslint-disable-next-line no-unused-vars
  const handleUpdateAdjustedQty = (branchName, adjustedQty) => {
    distributionService.updateAdjustedQty(branchName, adjustedQty)
      .then(response => {
        console.log("distributionService.updateAdjustedQty response ", response)
        if(response.rows_updated){
          console.log("distributionService.updateAdjustedQty response.items ", response.items)
          setIsUpdated(true);
        }else{
          setDistributionError("Не удалось редактировать");
        }
      })
      .catch(error => {
        console.log("distributionService.updateAdjustedQty error ", error)

        const errorMessage = parseApiError(error)
        setDistributionError(errorMessage ? errorMessage : "");
      })
      .finally(() => { setRefreshKey(k => k + 1); })
  }

  const handleDownloadExcel = async () => {
      setDownloading(true);
      const columnFilters = Object.fromEntries(
        COLUMNS.map(({ key }) => [key, filters[key]]).filter(([, v]) => v?.length)
      );
      try {
        const response = await distributionService.downloadExcel(filters.page, filters.page_size, filters.view_type, columnFilters, productFilters);
  
        // Create blob URL
        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });
  
        const url = window.URL.createObjectURL(blob);
  
        // Extract filename from header (if exists)
        const contentDisposition = response.headers["content-disposition"];
        let filename = "download.xlsx";
  
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="(.+)"/);
          if (match?.[1]) filename = match[1];
        }
  
        // Create temp link
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
  
        document.body.appendChild(link);
        link.click();
  
        // Cleanup
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Download failed", error);
      }
      setDownloading(false);
  }

  return (
    <>
      {isLoadingData && <LoadingAnimation />}
      <div className="page-content">
        <NotificationContainer>
          {
            isUpdated && <NotificationModal closeModal={() => setIsUpdated(false)} type="success" title="Успешно обновлен" duration={2000} />
          }
          {
            distributionError && <NotificationModal closeModal={() => setDistributionError(null)} type="error" subTitle={distributionError} title="Ошибка" duration={3000}/>
          }
        </NotificationContainer>
        <div className="page-header-container">
            <h1 className="page-title">Распределение по филиалам {planningDate && "на " + formatToAbbrevMonthYear(planningDate, true)}</h1>
            <div className='page-header-buttons'>
              <button type="button" className="primary-button" onClick={handleDownloadExcel}>
                <DownloadIcon size={24}/>
                {
                  downloading
                    ? <LoadingIcon size={20} color="#ffffff" className={"loading-icon spinning"}/>
                    : "Скачать Excel"
                }
                
              </button>
            </div>
        </div>
        
        <div className='page-subheading-row'>
          <div className='distribution-filters-container'>
            <button
              type="button"
              onClick={openProductsFilter}
              className="filter-select"
            >
              <span className="filter-select-label">Продукты:</span>
              <span className="filter-select-option">
                {productFiltersArray.join(", ")}
              </span>
              {productFiltersArray.length > 0 && (
                <span style={{ background: "#7CC805", color: "#fff", borderRadius: 20, fontSize: 11, fontWeight: 700, padding: "1px 6px", flexShrink: 0 }}>
                  {productFiltersArray.length}
                </span>
              )}
              <ArrowDownIcon size={24} color='#7CC805'/>
            </button>
              
            {
              isProductsFilterOpen &&
                <AccordionCheckboxSelector
                  selectedOptions={productFilters}
                  closeModal={() => setIsProductsFilterOpen(false)}
                  onApply={(selectedFilter) => updateFilters(selectedFilter)}
                />
            }
            
            <SegmentedControl
              value={filters.view_type?.toLowerCase()}
              onChange={(value) => updateFilters({ view_type: value })}
              options={[
                {
                  label: "Деньги",
                  options: [
                    { label: "Прайс-лист", value: "dsp" },
                    { label: "Инвойс цена", value: "invoice price" },
                  ],
                },
                { label: "Короб", value: "cases" },
              ]}
            />
          </div>
          
          <div className='distribution-info-cards'>

              <div className='distribution-info-card'>
                  {summaryInfo?.total_target_amount_dsp_text && (
                    <InfoTooltip>{summaryInfo.total_target_amount_dsp_text}</InfoTooltip>
                  )}
                  <span className='distribution-info-card-label'>
                    План в деньгах:
                  </span>
                  <span className='distribution-info-card-value'>
                    {numberToKiloFormat(distributionSummary?.total_target_amount_dsp)}
                  </span>
              </div>

              <div className='distribution-info-card'>
                  {summaryInfo?.total_fact_amount_dsp_text && (
                    <InfoTooltip>{summaryInfo.total_fact_amount_dsp_text}</InfoTooltip>
                  )}
                  <span className='distribution-info-card-label'>
                    Прогнозируемый Факт:
                  </span>
                  <span className='distribution-info-card-value'>
                    {numberToKiloFormat(distributionSummary?.total_fact_amount_dsp)}
                  </span>
              </div>

              <div className='distribution-info-card'>
                  {summaryInfo?.total_readiness_for_target && (
                    <InfoTooltip>{summaryInfo.total_readiness_for_target}</InfoTooltip>
                  )}
                  <span className='distribution-info-card-label'>
                    Общая готовность к плану:
                  </span>
                  <span className='distribution-info-card-value'>
                    {distributionSummary?.total_readiness_for_target}%
                  </span>
              </div>

          </div>

        </div>

        <div className="data-table-container">
          <table className="data-table distribution-table">
            <thead>
              <tr>
                <th>
                  <div style={{ display: 'flex', height: '100%', marginBottom: 'auto', gap: 6,  }}>
                    №
                  </div>
                </th>
                {COLUMNS.map(({ key, label }) => {
                  const hasFilter = key in filterOptions;
                  return (
                    <th key={key}>
                      <div style={{ display: 'flex', height: '100%', marginBottom: 'auto', gap: 6,  }}>
                        <span>{label}</span>
                        {hasFilter && (
                          <ColumnFilterDropdown
                            label={label}
                            options={colOptions[key] || []}
                            selected={getColSelected(key)}
                            onApply={(vals) => handleColFilterApply(key, vals)}
                          />
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {
                (distributionData && distributionData.length > 0) ? distributionData.map((row, mainIndex) => {
                  
                  const key = distributionKey(row, mainIndex);
                  // eslint-disable-next-line no-unused-vars
                  const isDistributionEdit = editingDistributionRows[key] !== undefined && editingDistributionRows[key] !== false;
                
                  return(
                    <tr key={mainIndex}>
                      <td>
                        <div className="distribution-table-column-number">{mainIndex + 1}</div>
                      </td>
                      <td
                        className='branch-name-cell'
                        onClick={() => {navigate(`/distribution/branch?branch_name=${row.branch_name}&hub_name=${row.hub_name}`)}}
                      >
                        <div className="distribution-table-column-branch">{row.branch_name}</div>
                      </td>
                      <td>
                        <div className="distribution-table-column-volume">{row.target_amount_dsp_per_branch?.toLocaleString("ru-RU")} </div>
                      </td>
                      <td>
                        <div className="distribution-table-column-amount">
                          {row.available_amount_kzt_per_branch?.toLocaleString("ru-RU")}
                        </div>
                      </td>
                      <td>
                        <div className="distribution-table-column-recommended">{row.recommended_amount_kzt_per_branch?.toLocaleString("ru-RU")} </div>
                      </td>
                      <td>
                        <div className="distribution-table-column-adjusted">{row.adjusted_amount_kzt_per_branch?.toLocaleString("ru-RU")} </div>
                        {/* <EditableCell
                          value={row.adjusted_amount_kzt_per_branch}
                          isEditing={isDistributionEdit}
                          onChange={(e) => {
                            e.stopPropagation();
                            setDistributionData(prev =>
                              prev.map((item, index) =>
                                index === mainIndex
                                  ? { ...item, adjusted_amount_kzt_per_branch: e.target.value }
                                  : item
                              )
                            );
                          }}
                          onEdit={() => setEditingDistributionRows(prev => ({ ...prev, [key]: row.adjusted_amount_kzt_per_branch }))}
                          onConfirm={() => {
                            handleUpdateAdjustedQty(row.branch_name, row.adjusted_amount_kzt_per_branch);
                            setEditingDistributionRows(prev => ({ ...prev, [key]: false }));
                          }}
                          onCancel={() => {
                            setDistributionData(prev => prev.map((item, i) =>
                              i === mainIndex ? { ...item, adjusted_amount_kzt_per_branch: editingDistributionRows[key] } : item
                            ));
                            setEditingDistributionRows(prev => ({ ...prev, [key]: false }));
                          }}
                          renderValue={(v) => v?.toLocaleString("ru-RU")}
                          displayContainerStyle={{maxWidth: "150px"}}
                          editingContainerStyle={{width: "auto"}}
                        /> */}
                      </td>
                      <td>
                        <div className="distribution-table-column-recommended">{row.total_adjusted_volume_cbm_per_branch?.toLocaleString("ru-RU")} </div>
                      </td>
                      <td>
                        <div className="distribution-table-column-recommended">{row.total_adjusted_gross_weight_kg_per_branch?.toLocaleString("ru-RU")} </div>
                      </td>
                      <td>
                        <BatteryCell value={row.readiness_for_target_per_branch} />
                      </td>
                    </tr>
                    )
                  }) : <tr key={"distribution-list-1"} >
                          <td style={{margin: "auto", textAlign: 'center'}} colSpan="9">Нет данных</td>
                      </tr>
              }
            </tbody>
          </table>
          <Pagination
            currentPage={filters.page}
            totalItems={distributionTotalItems}
            onPageChange={(p) => updateFilters({page: p})}
            rowsPerPage={filters.page_size}
            onRowsPerPageChange={(r) => { updateFilters({page_size: r}) }}
            rowsPerPageOptions={[5, 10, 20, 50]}
          />
        </div>
        
      </div>
    </>
  );
}

